import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Bold01, Button, Checkbox, Link01, TextInput, Trash01 } from "@opendate-app/setlist";

const ConsentItem = ({ consent, editorRefs, onChange, onBold, onReq, onChk, onRemove }) => {
  const ref = useRef(null);
  const savedRange = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  useEffect(() => {
    editorRefs.current[consent.cid] = null;
    if (ref.current) { ref.current.innerHTML = consent.text || ""; editorRefs.current[consent.cid] = ref.current; }
    return () => { delete editorRefs.current[consent.cid]; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openLink = () => {
    if (linkOpen) { setLinkOpen(false); return; }
    const sel = window.getSelection();
    savedRange.current = sel && sel.rangeCount && ref.current?.contains(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null;
    setLinkUrl("");
    setLinkOpen(true);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    const editor = ref.current;
    setLinkOpen(false);
    if (!url || !editor) return;
    editor.focus();
    const sel = window.getSelection();
    if (savedRange.current) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
    if (sel.isCollapsed) {
      document.execCommand("insertHTML", false, `<a href="${url.replace(/"/g, "&quot;")}">${url}</a>`);
    } else {
      document.execCommand("createLink", false, url);
    }
    onChange(editor.innerHTML);
  };

  return (
    <div className="sf-consent-item">
      <div className="sf-wysiwyg-toolbar">
        <Button variant="tertiary" size="xs" icon={Bold01} aria-label="Bold" title="Bold"
          onMouseDown={(e) => e.preventDefault()} onClick={onBold} />
        <Button variant="tertiary" size="xs" icon={Link01} aria-label="Add link" title="Add link" aria-expanded={linkOpen}
          onMouseDown={(e) => e.preventDefault()} onClick={openLink} />
      </div>
      {linkOpen && (
        <div className="sf-link-row">
          <TextInput size="sm" type="url" autoFocus aria-label="Link URL" placeholder="https://yourvenue.com/terms"
            value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); applyLink(); }
              if (e.key === "Escape") setLinkOpen(false);
            }} />
          <Button variant="primary" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={applyLink}>Add link</Button>
        </div>
      )}
      <div className="sf-wysiwyg-editor" contentEditable suppressContentEditableWarning data-placeholder="Checkbox text…"
        role="textbox" aria-multiline="true" aria-label="Checkbox text"
        ref={ref} onInput={() => onChange(ref.current.innerHTML)} />
      <div className="sf-consent-controls">
        {onReq && (
          <Checkbox size="sm" label="Required to submit" checked={!!consent.required} onChange={(e) => onReq(e.target.checked)} />
        )}
        {onChk && (
          <Checkbox size="sm" label="Checked by default" checked={!!consent.checked} onChange={(e) => onChk(e.target.checked)} />
        )}
        <span className="sf-consent-remove">
          <Button variant="link-destructive" size="sm" icon={Trash01} onClick={onRemove}>Remove</Button>
        </span>
      </div>
    </div>
  );
};

ConsentItem.propTypes = {
  consent: PropTypes.shape({
    cid: PropTypes.number.isRequired,
    text: PropTypes.string,
    required: PropTypes.bool,
    checked: PropTypes.bool,
    channel: PropTypes.string,
  }).isRequired,
  editorRefs: PropTypes.shape({ current: PropTypes.object }).isRequired,
  onChange: PropTypes.func.isRequired,
  onBold: PropTypes.func.isRequired,
  onReq: PropTypes.func,
  onChk: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
};

export default ConsentItem;
